import { Routes } from '@angular/router';
import { RoleGuard } from '../../core/guard/role.guard';
import { SingleDepartmentService } from '../../shared/services/single-department.service';
import { PalletViewRegisterApiService } from '../../views/pallets/register-pallet/api/view.service';
import { PalletViewDataDisplayAuthService } from '../../views/pallets/data-display/services/auth.service';
import { ParamsTypeGuard } from '../../core/guard/params-type.guard';

import { tanksAccionesRoles } from 'src/app/settings/acciones-roles/departamentos/tanks-acciones-roles';
import {
  TankDepartmentService,
  TankPalletViewRegisterApiService,
  TanksViewDataDisplayAuthService,
  TanksPackingBoxAuthService,
} from '@dep-tanks/services';
import { PLASTIC_PART_STAGE_GROUP } from '@dep-tanks/view-scan-scrap/token/token';
import { PackingBoxViewAuthService } from '../../views/packing-box/services/auth.service';
import { PackingBoxViewService } from '../../views/packing-box/services/view.service';
import { PackingBoxViewApiService } from '../../views/packing-box/api/view.service';
import { PackingBoxViewZebraApiService } from '../../views/packing-box/api/view-zebra.service';
import { PackingBoxZebraTableService } from '../../views/packing-box/components/table/table-zebra.service';
import { WebSocketApiLabelService } from '../../api/elite/websockets/label.service';
import { TableService } from '../../core/components/table/table.service';

const PALLET_BASE_DATA = {
  breadcrumb: 'Pallet',
  department: 'Tanks',
  expectedRole: tanksAccionesRoles.sharedActions.palletRegistration,
};

const PART_SCRAP_PROVIDERS = [
  { provide: SingleDepartmentService, useClass: TankDepartmentService },
];

const PART_BASE_DATA = {
  breadcrumb: 'Plastic parts',
  department: 'Tanks',
  expectedRole: tanksAccionesRoles.sharedActions.scanPartScrap,
};

const routes: Routes = [
  { path: '', pathMatch: 'full', redirectTo: 'box' },
  {
    path: 'data',
    loadComponent: () =>
      import('../../views/pallets/data-display/data-display.component').then(
        (m) => m.PalletViewDataDisplayComponent
      ),
    canActivate: [RoleGuard],
    data: {
      breadcrumb: 'Pallets',
      pageTitle: 'Lista de Pallets',
      expectedRole: tanksAccionesRoles.routerActions.viewData,
      department: 'Tanks',
    },
  },

  {
    path: 'pallet',
    loadComponent: () =>
      import(
        '../../views/pallets/register-pallet/register-pallet.component'
      ).then((m) => m.ViewRegisterPalletComponent),
    canActivate: [RoleGuard],
    data: {
      ...PALLET_BASE_DATA,
      pageTitle: 'Registrar pallet - Selecciona o crea un equipo',
    },
    children: [
      {
        path: ':teamId/:palletId',
        loadComponent: () =>
          import(
            '../../views/pallets/register-pallet/register-pallet.component'
          ).then((m) => m.ViewRegisterPalletComponent),
        canActivate: [ParamsTypeGuard],
        data: {
          pageTitle: 'Registrar pallet - Scaneo de pallet',
          department: 'Tanks',
          expectedRole: tanksAccionesRoles.sharedActions.palletRegistration,
          addReturnButton: true,
          paramValidation: {
            teamId: { type: 'number' },
            palletId: { type: 'number' },
          },
        },
      },
    ],
  },

  {
    path: 'pallet/:teamId',
    loadComponent: () =>
      import(
        '../../views/pallets/register-pallet/register-pallet.component'
      ).then((m) => m.ViewRegisterPalletComponent),
    canActivate: [RoleGuard, ParamsTypeGuard],
    data: {
      ...PALLET_BASE_DATA,
      pageTitle: 'Registrar pallet - Escaneo de pallet',
      addReturnButton: true,
      paramValidation: {
        teamId: { type: 'number' },
      },
    },
  },

  {
    path: 'box',
    loadComponent: () =>
      import('./view-scan-wo/view-scan-wo.component').then(
        (m) => m.TankViewScanWoComponent
      ),
    canActivate: [RoleGuard],
    data: {
      pageTitle: 'Packing box',
      breadcrumb: 'Tanks',
      expectedRole: tanksAccionesRoles.sharedActions.scanPackingBox,
    },
    children: [
      {
        path: ':teamId/:workOrder',
        loadComponent: () =>
          import(
            './view-scan-wo/components/scan-assembly/scan-assembly.component'
          ).then((m) => m.TankScanAssemblyComponent),
        canActivate: [ParamsTypeGuard],
        data: {
          pageTitle: 'Escaneo de packing box',
          addReturnButton: true,
          department: 'Tanks',
          paramValidation: {
            teamId: { type: 'number' },
            workOrder: { type: 'string' },
          },
        },
      },
    ],
  },
  {
    path: 'box/:teamId',
    loadComponent: () =>
      import('./view-scan-wo/view-scan-wo.component').then(
        (m) => m.TankViewScanWoComponent
      ),
    canActivate: [RoleGuard, ParamsTypeGuard],
    data: {
      pageTitle: 'Packing box',
      breadcrumb: 'Tanks',
      expectedRole: tanksAccionesRoles.sharedActions.scanPackingBox,
      addReturnButton: true,
      department: 'Tanks',
      paramValidation: {
        teamId: { type: 'number' },
      },
    },
  },

  {
    path: 'packing-box',
    loadComponent: () =>
      import('../../views/packing-box/packing-box.component').then(
        (m) => m.PackingBoxComponent
      ),
    canActivate: [RoleGuard],
    providers: [
      {
        provide: PackingBoxViewAuthService,
        useClass: TanksPackingBoxAuthService,
      },
      { provide: TableService, useClass: PackingBoxZebraTableService },
      WebSocketApiLabelService,
      PackingBoxViewService,
      {
        provide: PackingBoxViewApiService,
        useClass: PackingBoxViewZebraApiService,
      },
    ],
    data: {
      breadcrumb: 'Packing Box',
      department: 'Tanks',
      pageTitle: 'Crear etiquetas',
      companyType: 'sb',
      expectedRole: tanksAccionesRoles.sharedActions.packingBox,
    },
  },

  {
    path: 'part-scrap',
    loadComponent: () =>
      import('./view-scan-scrap/view-scan-scrap.component').then(
        (m) => m.TankViewScanScrapComponent
      ),
    canActivate: [RoleGuard],
    data: {
      ...PART_BASE_DATA,
      pageTitle: 'Registrar scrap - Selecciona o crea un equipo',
    },
    providers: [...PART_SCRAP_PROVIDERS],
  },
  {
    path: 'part-scrap/:teamId',
    loadComponent: () =>
      import('./view-scan-scrap/view-scan-scrap.component').then(
        (m) => m.TankViewScanScrapComponent
      ),
    canActivate: [RoleGuard, ParamsTypeGuard],
    providers: [
      ...PART_SCRAP_PROVIDERS,
      {
        provide: PLASTIC_PART_STAGE_GROUP,
        useValue: {
          stageName: 'Ensamble',
          nextStage: 'Inspeccion final',
        },
      },
    ],
    data: {
      ...PART_BASE_DATA,
      pageTitle: 'Registrar scrap',
      addReturnButton: true,
      paramValidation: {
        teamId: { type: 'number' },
      },
    },
  },

  {
    path: 'part-scrap-final',
    loadComponent: () =>
      import('./view-scan-scrap/view-scan-scrap.component').then(
        (m) => m.TankViewScanScrapComponent
      ),
    canActivate: [RoleGuard],
    data: {
      ...PART_BASE_DATA,
      pageTitle: 'Registrar scrap - Selecciona o crea un equipo',
    },
    providers: [...PART_SCRAP_PROVIDERS],
  },
  {
    path: 'part-scrap-final/:teamId',
    loadComponent: () =>
      import('./view-scan-scrap/view-scan-scrap.component').then(
        (m) => m.TankViewScanScrapComponent
      ),
    canActivate: [RoleGuard, ParamsTypeGuard],
    providers: [
      ...PART_SCRAP_PROVIDERS,
      {
        provide: PLASTIC_PART_STAGE_GROUP,
        useValue: {
          stageName: 'Inspeccion final',
          nextStage: 'Ready for assembly',
          stageReport: 'Ensamble',
        },
      },
    ],
    data: {
      ...PART_BASE_DATA,
      pageTitle: 'Registrar scrap',
      addReturnButton: true,
      paramValidation: {
        teamId: { type: 'number' },
      },
    },
  },
  {
    path: 'validation',
    loadComponent: () =>
      import('./view-validation/view-validation.component').then(
        (m) => m.TankViewValidationComponent
      ),
    canActivate: [RoleGuard],
    data: {
      pageTitle: 'Validación de pallets',
      breadcrumb: 'Form',
      expectedRole: tanksAccionesRoles.sharedActions.validatePallets,
    },
  },
];
export const TanksDepartmentRoutes = {
  path: 'tank',
  children: routes,
  providers: [
    {
      provide: SingleDepartmentService,
      useClass: TankDepartmentService,
    },
    {
      provide: PalletViewRegisterApiService,
      useClass: TankPalletViewRegisterApiService,
    },
    {
      provide: PalletViewDataDisplayAuthService,
      useClass: TanksViewDataDisplayAuthService,
    },
  ],
};
